<script lang="ts">
  import { link, push } from "svelte-spa-router";
  import { resetPassword, ApiError } from "../lib/api";
  import { recoverWithCode } from "../lib/e2eeSession";
  import { t, _ } from "../lib/i18n";

  /** Route params injected by svelte-spa-router: { token }. */
  export let params: { token?: string } = {};

  let password = "";
  let confirm = "";
  let submitting = false;
  let error: string | null = null;
  let done = false;

  // Optional inline recovery after the reset: a token reset cannot re-wrap the
  // old key (old password unknown), so offer to restore it from a recovery code.
  // We re-wrap the recovered key under the password just set so future logins
  // unwrap normally.
  let recoveryCode = "";
  let recoverySubmitting = false;
  let recoveryMsg: { kind: "ok" | "error"; text: string } | null = null;

  async function submitRecovery() {
    if (!recoveryCode) return;
    recoverySubmitting = true;
    recoveryMsg = null;
    try {
      const ok = await recoverWithCode(recoveryCode, password);
      recoveryMsg = ok
        ? { kind: "ok", text: t("reset.recoveryOk") }
        : { kind: "error", text: t("reset.recoveryFail") };
    } catch {
      recoveryMsg = { kind: "error", text: t("reset.recoveryFail") };
    } finally {
      recoverySubmitting = false;
    }
  }

  async function submit(e: Event) {
    e.preventDefault();
    error = null;
    if (!password) return;
    if (password !== confirm) {
      error = t("reset.mismatch");
      return;
    }
    const token = params.token ?? "";
    if (!token) {
      error = t("reset.invalid");
      return;
    }
    submitting = true;
    try {
      await resetPassword(token, { password });
      // E2EE NOTE (docs/e2ee.md:102-112): a token-based RESET has no access to
      // the OLD password, so the password-wrapped private key cannot be
      // re-wrapped here and old shared keys become unreadable. Recovery is NOT
      // attempted — on the NEXT login, e2eeSession.provisionIdentity() detects
      // the unwrap failure and provisions a FRESH identity (keys must be
      // re-shared). For an AUTHENTICATED in-app password change (which DOES know
      // the old password), call rewrapIdentityForNewPassword(oldPw, newPw) from
      // e2eeSession.ts right after the change succeeds to preserve the identity.
      done = true;
      // NOTE: no auto-redirect here — the student may want to enter a recovery
      // code to rescue the existing encryption key before continuing.
    } catch (err) {
      error =
        err instanceof ApiError && err.message === "invalid_or_expired_token"
          ? t("reset.invalid")
          : err instanceof ApiError
            ? err.message
            : t("login.error.failed");
    } finally {
      submitting = false;
    }
  }
</script>

<section class="login">
  <div class="card">
    <h1>{$_("reset.newTitle")}</h1>
    {#if done}
      <p class="sub" data-testid="reset-done">{$_("reset.done")}</p>
      <div class="recovery" data-testid="reset-recovery">
        <h2>{$_("reset.recoveryTitle")}</h2>
        <p class="hint">{$_("reset.recoveryInfo")}</p>
        <label>
          {$_("reset.recoveryCode")}
          <input
            type="text"
            bind:value={recoveryCode}
            data-testid="reset-recovery-code"
          />
        </label>
        <button
          type="button"
          class="ghost"
          data-testid="reset-recovery-submit"
          disabled={recoverySubmitting}
          on:click={submitRecovery}
        >
          {$_("reset.recoverySubmit")}
        </button>
        {#if recoveryMsg}
          <p
            class={recoveryMsg.kind === "ok" ? "ok" : "error"}
            data-testid="reset-recovery-msg"
          >
            {recoveryMsg.text}
          </p>
        {/if}
      </div>
      <button
        type="button"
        class="continue"
        data-testid="reset-continue"
        on:click={() => push("/")}
      >
        {$_("account.back")}
      </button>
    {:else}
      <p class="warn" data-testid="reset-e2ee-warn">{$_("reset.e2eeWarn")}</p>
      <form on:submit={submit}>
        <label>
          {$_("reset.newPassword")}
          <input
            type="password"
            autocomplete="new-password"
            bind:value={password}
            data-testid="new-password"
            required
          />
        </label>
        <label>
          {$_("reset.confirm")}
          <input
            type="password"
            autocomplete="new-password"
            bind:value={confirm}
            data-testid="confirm-password"
            required
          />
        </label>
        <button type="submit" disabled={submitting} data-testid="reset-submit">
          {submitting ? $_("reset.saving") : $_("reset.save")}
        </button>
        {#if error}<p class="error" data-testid="reset-error">{error}</p>{/if}
      </form>
      <a class="forgot" href="/" use:link>{$_("account.back")}</a>
    {/if}
  </div>
</section>

<style>
  .login {
    min-height: 100vh;
    display: grid;
    place-items: center;
    padding: 1rem;
    /* iOS viewport-fit=cover: keep the card out from under the status bar and
       home indicator. env() is 0 elsewhere, so 1rem is preserved. */
    padding-top: max(1rem, env(safe-area-inset-top));
    padding-bottom: max(1rem, env(safe-area-inset-bottom));
  }
  .card {
    width: 100%;
    max-width: 360px;
    background: #111827;
    border: 1px solid #374151;
    border-radius: 12px;
    padding: 1.5rem;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
  }
  h1 {
    margin: 0 0 0.25rem;
    font-size: 1.5rem;
  }
  .sub {
    margin: 0 0 1rem;
    color: #9ca3af;
  }
  form {
    display: grid;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
  label {
    display: grid;
    gap: 0.25rem;
    font-size: 0.875rem;
    color: #d1d5db;
  }
  input {
    padding: 0.6rem 0.75rem;
    background: #1f2937;
    color: #f3f4f6;
    border: 1px solid #374151;
    border-radius: 8px;
    font-size: 1rem;
  }
  button {
    padding: 0.6rem 0.75rem;
    background: #10b981;
    color: #04221a;
    font-weight: 600;
    border: none;
    border-radius: 8px;
    font-size: 1rem;
  }
  .error {
    margin: 0;
    color: #fca5a5;
    font-size: 0.875rem;
  }
  .ok {
    margin: 0;
    color: #6ee7b7;
    font-size: 0.875rem;
  }
  .warn {
    margin: 0 0 1rem;
    padding: 0.6rem 0.7rem;
    background: rgba(252, 211, 77, 0.1);
    border: 1px solid #b45309;
    border-radius: 8px;
    color: #fcd34d;
    font-size: 0.8rem;
  }
  .hint {
    margin: 0 0 0.5rem;
    color: #9ca3af;
    font-size: 0.82rem;
  }
  .recovery {
    display: grid;
    gap: 0.5rem;
    margin: 1rem 0;
    padding-top: 1rem;
    border-top: 1px solid #374151;
  }
  .recovery h2 {
    margin: 0;
    font-size: 1rem;
  }
  .recovery .ghost {
    background: transparent;
    border: 1px solid #374151;
    color: #f3f4f6;
  }
  .continue {
    margin-top: 0.5rem;
    background: #10b981;
    color: #04221a;
  }
  .forgot {
    display: inline-block;
    color: #6ee7b7;
    font-size: 0.85rem;
    text-decoration: none;
  }
  .forgot:hover {
    text-decoration: underline;
  }
</style>
